import React from "react";
import { useFormikContext } from "formik";
import { getFieldPath, getToggleProps, integerTransformer } from "utils/form";
import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep";
import Toggle from "react-toggle";
import { FORM_KEY } from "utils/constants/form";
import ConnectedField from "components/ConnectedField";
import HoverIconButton from "components/HoverIconButton";
import SectionBezel from "components/SectionBezel";
import { INPUT_TYPE } from "utils/constants/ui";
import SelectField from "components/SelectField";
import isEqual from "lodash/isEqual";
import startCase from "lodash/startCase";
import { useAppCheckout } from "utils/selectors";
import { gridContainer } from "constants/styles";
import Tabs, { MINI_TABS_PROPS } from "components/Tabs";
import {
  getShippingDeliveryEstimateLabel,
  templateShippingRateDeliveryEstimate
} from "utils/shippingRate";
import classnames from "classnames";

const DELIVERY_ESTIMATE = "delivery_estimate";

const DeliveryEstimateTemplateGridPicker = ({ name }) => {
  const checkout = useAppCheckout();
  const { values, setFieldValue } = useFormikContext();
  const deliveryEstimateTemplates =
    get(checkout, "shipping.deliveryEstimate.templates") || [];
  const deliveryEstimateActivePath = getFieldPath(
    name,
    `${FORM_KEY}.${DELIVERY_ESTIMATE}`
  );

  const deliveryEstimateActive = get(values, deliveryEstimateActivePath);
  const deliveryEstimatePath = getFieldPath(name, DELIVERY_ESTIMATE);
  const currentEstimate = get(values, deliveryEstimatePath);

  return deliveryEstimateTemplates.map((template, templateIx) => {
    const templateEstimate = template.delivery_estimate;
    const isActive = isEqual(currentEstimate, templateEstimate);

    return (
      <div
        key={templateIx}
        onClick={() => {
          if (deliveryEstimateActive) {
            setFieldValue(deliveryEstimatePath, templateEstimate);
          }
        }}
        className={classnames("pa3 ba br2", {
          "b--primary": isActive,
          "hairline-1": !isActive,
          "bg-white--hover pointer": deliveryEstimateActive,
          "o-50": !deliveryEstimateActive
        })}
      >
        <div className="fw5 f6 lh-copy">{template.display_name}</div>
      </div>
    );
  });
};

const DeliveryEstimateFields = ({ name }) => {
  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldError,
    setFieldTouched
  } = useFormikContext();

  const checkout = useAppCheckout();

  const deliveryEstimateActivePath = getFieldPath(
    name,
    `${FORM_KEY}.${DELIVERY_ESTIMATE}`
  );
  const deliveryEstimateActive = get(values, deliveryEstimateActivePath);

  const deliveryEstimatePath = getFieldPath(name, DELIVERY_ESTIMATE);
  const deliveryEstimate = get(values, deliveryEstimatePath);

  const minDeliveryEstimateValuePath = getFieldPath(
    name,
    `${DELIVERY_ESTIMATE}.minimum.value`
  );
  const maxDeliveryEstimateValuePath = getFieldPath(
    name,
    `${DELIVERY_ESTIMATE}.maximum.value`
  );

  const minDeliveryEstimateUnitPath = getFieldPath(
    name,
    `${DELIVERY_ESTIMATE}.minimum.unit`
  );
  const maxDeliveryEstimateUnitPath = getFieldPath(
    name,
    `${DELIVERY_ESTIMATE}.maximum.unit`
  );

  const deliveryEstimateUnits =
    get(checkout, "shipping.deliveryEstimate.units") || [];
  const unitOptions = deliveryEstimateUnits.map((unit) => ({
    uid: unit,
    label: startCase(unit)
  }));

  const deliveryEstimateTouched = get(touched, deliveryEstimateActivePath);

  const idPath = getFieldPath(name, "id");
  const id = get(values, idPath);
  const editMode = Boolean(id);
  let content = null;
  let toggleDeliveryEstimate = null;
  if (!editMode) {
    /**
     * Reset the delivery_estimate state for:
     * - values
     * - errors
     * - touched
     */
    const resetDeliveryEstimate = () => {
      const clonedRate = cloneDeep(get(values, name));
      clonedRate[DELIVERY_ESTIMATE] = templateShippingRateDeliveryEstimate();

      setFieldValue(name, clonedRate);
      setFieldError(deliveryEstimatePath, null);
      setFieldTouched(deliveryEstimatePath, null);
    };

    const toggleEstimate = (_, active) => {
      setFieldValue(deliveryEstimateActivePath, active);
      if (!active) {
        const clonedRate = cloneDeep(get(values, name));
        clonedRate[DELIVERY_ESTIMATE] = templateShippingRateDeliveryEstimate();
        clonedRate[FORM_KEY][DELIVERY_ESTIMATE] = active;
        setFieldValue(name, clonedRate);
      } else {
        setFieldValue(deliveryEstimateActivePath, active);
      }

      if (!deliveryEstimateTouched) {
        setFieldTouched(deliveryEstimateActivePath, true);
      }
    };

    toggleDeliveryEstimate = (
      <div className="flex flex-row items-center">
        <div className="pr3">
          <HoverIconButton
            type="refresh"
            tooltip="Reset"
            disabled={editMode}
            onClick={resetDeliveryEstimate}
          />
        </div>
        <Toggle
          {...getToggleProps({
            active: deliveryEstimateActive,
            disabled: editMode,
            setFieldValue: toggleEstimate
          })}
        />
      </div>
    );

    const minValError = get(errors, minDeliveryEstimateValuePath);
    const minValTouched = get(touched, minDeliveryEstimateValuePath);

    const maxValError = get(errors, maxDeliveryEstimateValuePath);
    const maxValTouched = get(touched, maxDeliveryEstimateValuePath);

    const tabProps = {
      ...MINI_TABS_PROPS,
      tabs: [
        {
          name: "Templates",
          id: "templates",
          node: (
            <div className="pt3">
              <div className="pa3 grid-container-4">
                <DeliveryEstimateTemplateGridPicker name={name} />
                <style jsx>{gridContainer}</style>
              </div>
            </div>
          )
        },
        {
          name: "Custom",
          id: "custom",
          node: (
            <div className="pt2">
              <div className="pa3 mt2">
                <div className="w-100">
                  <div className="flex flex-row justify-between items-center pb2">
                    <label className="field__label fw5 db">Between</label>
                    <div className="flex flex-row items-center justify-end">
                      <div className="pr2">
                        <div className="w3">
                          <ConnectedField
                            disabled={!deliveryEstimateActive || editMode}
                            name={minDeliveryEstimateValuePath}
                            inputMode={INPUT_TYPE.NUMERIC}
                            hideError
                            transformer={integerTransformer}
                          />
                        </div>
                      </div>
                      <div className="w4">
                        <SelectField
                          disabled={!deliveryEstimateActive || editMode}
                          name={minDeliveryEstimateUnitPath}
                          options={unitOptions}
                          customClasses={{ label: "field__label fw5 db" }}
                        />
                      </div>
                    </div>
                  </div>
                  {minValError && minValTouched && (
                    <div className="f7 fw5 red tr">{minValError}</div>
                  )}
                </div>
                <div className="w-100 pt3">
                  <div className="flex flex-row justify-between items-center pb2">
                    <label className="field__label fw5 db flex">and</label>
                    <div className="flex flex-row items-center justify-end">
                      <div className="pr2">
                        <div className="w3">
                          <ConnectedField
                            disabled={!deliveryEstimateActive || editMode}
                            name={maxDeliveryEstimateValuePath}
                            inputMode={INPUT_TYPE.NUMERIC}
                            hideError
                            transformer={integerTransformer}
                          />
                        </div>
                      </div>
                      <div className="w4">
                        <SelectField
                          disabled={!deliveryEstimateActive || editMode}
                          name={maxDeliveryEstimateUnitPath}
                          options={unitOptions}
                          customClasses={{ label: "field__label fw5 db" }}
                        />
                      </div>
                    </div>
                  </div>
                  {maxValError && maxValTouched && (
                    <div className="f7 fw5 red tr">{maxValError}</div>
                  )}
                </div>
              </div>
            </div>
          )
        }
      ]
    };
    content = <Tabs {...tabProps} />;
  } else if (deliveryEstimate) {
    const estimate = getShippingDeliveryEstimateLabel(
      { delivery_estimate: deliveryEstimate },
      {
        parentheses: false
      }
    );
    content = <div className="f5 fw5 ph3">{estimate}</div>;
  }

  return (
    <SectionBezel
      customClasses={{
        content: "list pv3 ma0 w-100"
      }}
      header="Estimated shipping time"
      action={toggleDeliveryEstimate}
    >
      {content}
    </SectionBezel>
  );
};

export default DeliveryEstimateFields;
